import {
  BarChart, Bar, LineChart, Line, Cell,
  XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
} from "recharts";
import {
  Phone, Globe, Navigation, CalendarCheck, MessageSquare, ShoppingBag,
  TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { ActionsData } from "../types/PerformanceTypes";
import { C, METRIC_COLORS, METRIC_LABELS, AXIS } from "../../../constants/constants";
import { buildSeries, fmtValue, tickFmt } from "../utils/PerformanceUtils";
import Card from "../ui/Card";
import SectionTitle from "../ui/SectionTitle";
import ChartTooltip from "../ui/ChartTooltip";

const ACT_LINE_KEYS = [
  "CALL_CLICKS", "WEBSITE_CLICKS", "DIRECTION_REQUESTS", "BUSINESS_BOOKINGS",
];

// ── Delta badge ──────────────────────────────────────────────────────────────

const DeltaBadge = ({ current, previous }: { current: number; previous: number }) => {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up  = pct > 0;
  const eq  = pct === 0;
  if (eq) return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-400">
      <Minus size={9} /> 0%
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
      ${up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
      {up ? <TrendingUp size={9} strokeWidth={2.5} /> : <TrendingDown size={9} strokeWidth={2.5} />}
      {up ? "+" : ""}{pct}%
    </span>
  );
};

// ── Props ────────────────────────────────────────────────────────────────────

interface ActionsTabProps {
  data:                ActionsData;
  compareEnabled?:     boolean;
  compareData?:        ActionsData;
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;
}

// ── Main ─────────────────────────────────────────────────────────────────────

const ActionsTab = ({
  data,
  compareEnabled,
  compareData,
  primaryPeriodLabel  = "Période A",
  comparePeriodLabel  = "Période B",
}: ActionsTabProps) => {
  const { totals, series } = data;

  const actionDefs = [
    { key: "callClicks",        name: "Appels",        color: C.success, icon: Phone         },
    { key: "websiteClicks",     name: "Clics site",    color: C.primary, icon: Globe         },
    { key: "directionRequests", name: "Itinéraires",   color: C.accent,  icon: Navigation    },
    { key: "bookings",          name: "Réservations",  color: C.purple,  icon: CalendarCheck },
    { key: "conversations",     name: "Conversations", color: "#ea580c", icon: MessageSquare },
    { key: "foodOrders",        name: "Commandes",     color: "#65a30d", icon: ShoppingBag   },
  ] as const;

  const activeActions = actionDefs.filter(d => (totals as any)[d.key] > 0);

  // Grouped bar data
  const barData = activeActions.map(d => ({
    name:     d.name,
    current:  (totals as any)[d.key] as number,
    previous: compareData ? (compareData.totals as any)[d.key] as number : 0,
    color:    d.color,
  }));

  // Line data (merged)
  const lineData    = buildSeries(series, ACT_LINE_KEYS);
  const cmpLineData = compareEnabled && compareData ? buildSeries(compareData.series, ACT_LINE_KEYS) : [];
  const maxLen      = Math.max(lineData.length, cmpLineData.length);
  const mergedLine  = Array.from({ length: maxLen }, (_, i) => ({
    ...(lineData[i] || { date: `J+${i}` }),
    ...Object.fromEntries(ACT_LINE_KEYS.map(k => [`cmp_${k}`, cmpLineData[i]?.[k] ?? null])),
  }));

  return (
    <div className="flex flex-col gap-6 animate-[gbpFadeIn_0.2s_ease]">

      {/* ── KPI cards with delta ── */}
      <div className="flex flex-wrap gap-3">
        {activeActions.map((d) => {
          const Icon    = d.icon;
          const current  = (totals as any)[d.key] as number;
          const previous = compareData ? (compareData.totals as any)[d.key] as number : undefined;
          return (
            <div
              key={d.key}
              className="relative flex-[1_1_140px] min-w-[130px] bg-white rounded-xl border border-slate-100
                         shadow-sm p-4 flex flex-col gap-1.5 hover:shadow-md transition-shadow duration-200"
            >
              {/* Icon + delta row */}
              <div className="flex items-start justify-between">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{ background: `${d.color}18` }}>
                  <Icon size={15} color={d.color} strokeWidth={2} />
                </div>
                {compareEnabled && previous !== undefined && (
                  <DeltaBadge current={current} previous={previous} />
                )}
              </div>

              {/* Value */}
              <p className="text-[22px] font-extrabold text-slate-900 m-0 leading-none tracking-tight">
                {fmtValue(current)}
              </p>
              <p className="text-[11px] text-slate-400 font-medium m-0">{d.name}</p>

              {/* Compare value */}
              {compareEnabled && previous !== undefined && (
                <p className="text-[11px] text-slate-400 m-0 pt-1 border-t border-slate-50">
                  vs <span className="font-semibold text-slate-500">{fmtValue(previous)}</span>
                  <span className="ml-1 text-violet-400 text-[10px]">{comparePeriodLabel}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Grouped bar ── */}
      <Card>
        <div className="flex items-center justify-between mb-1">
          <SectionTitle>Total par type d'action</SectionTitle>
          {compareEnabled && (
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1.5 font-semibold text-blue-600">
                <span className="w-3 h-3 rounded-sm bg-blue-500 opacity-90 inline-block" />
                {primaryPeriodLabel}
              </span>
              <span className="flex items-center gap-1.5 font-semibold text-violet-400">
                <span className="w-3 h-3 rounded-sm bg-violet-400 opacity-50 inline-block" />
                {comparePeriodLabel}
              </span>
            </div>
          )}
        </div>
       <ResponsiveContainer width="100%" height={200}>
          <BarChart data={barData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="name" {...AXIS} />
            <YAxis {...AXIS} tickFormatter={tickFmt} />
            <ChartTooltip />
            {compareEnabled && (
              <Legend iconType="circle" iconSize={7}
                      wrapperStyle={{ fontSize: 11, color: "#64748b" }} />
            )}
            <Bar dataKey="current" name={primaryPeriodLabel} radius={[5, 5, 0, 0]}>
              {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Bar>
            {compareEnabled && (
              <Bar dataKey="previous" name={comparePeriodLabel} radius={[5, 5, 0, 0]} opacity={0.4}>
                {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Line chart ── */}
      <Card>
        <div className="flex items-center justify-between mb-1">
          <SectionTitle>Évolution des actions dans le temps</SectionTitle>
          {compareEnabled && (
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1.5 text-blue-600 font-semibold">
                <span className="w-5 border-t-2 border-blue-500 inline-block" />
                {primaryPeriodLabel}
              </span>
              <span className="flex items-center gap-1.5 text-violet-400 font-semibold">
                <span className="w-5 border-t-2 border-dashed border-violet-400 inline-block" />
                {comparePeriodLabel}
              </span>
            </div>
          )}
        </div>
       <ResponsiveContainer width="100%" height={200}>
          <LineChart data={mergedLine} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" {...AXIS} />
            <YAxis {...AXIS} tickFormatter={tickFmt} />
            <ChartTooltip />
            <Legend iconType="circle" iconSize={7}
                    wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
            {ACT_LINE_KEYS.map((k) => (
              <Line key={k} type="monotone" dataKey={k}
                    name={METRIC_LABELS[k]} stroke={METRIC_COLORS[k]}
                    dot={false} strokeWidth={2} />
            ))}
            {compareEnabled && ACT_LINE_KEYS.map((k) => (
              <Line key={`cmp_${k}`} type="monotone" dataKey={`cmp_${k}`}
                    stroke={METRIC_COLORS[k]} strokeDasharray="4 3"
                    strokeOpacity={0.45} dot={false} strokeWidth={1.5}
                    legendType="none" name={`${METRIC_LABELS[k]} (B)`} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

    </div>
  );
};

export default ActionsTab;